/**
 * Error carrying the backend `errorCode`, so the UI can show a translated message instead of the
 * raw English one the API sends. `message` stays populated as the last-resort fallback.
 */
export class ApiError extends Error {
  readonly errorCode: string | null;
  readonly status: number;

  constructor(message: string, errorCode: string | null, status: number) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.status = status;
  }
}

/**
 * The parsed `{ success, data, error, errorCode, meta }` envelope, or the `ApiError` it describes.
 * One error path for both unwrappers, so opting into `meta` cannot drift on failure semantics.
 */
const envelope = async (res: Response, fallbackMessage: string): Promise<any> => {
  let body: any = {};
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok || body?.success === false) {
    // `error` is normally a string, but a non-string payload must not stringify to
    // "[object Object]" — fall back instead. The HTTP status is what makes a failed
    // request diagnosable (401 vs 403 vs 502), so keep it on transport errors.
    const message = typeof body?.error === 'string' && body.error ? body.error : fallbackMessage;
    const errorCode = typeof body?.errorCode === 'string' ? body.errorCode : null;
    throw new ApiError(res.ok ? message : `${message} (HTTP ${res.status})`, errorCode, res.status);
  }
  return body;
};

// Unwrap the `{ success, data, error, errorCode }` envelope returned by the API.
// Throws an ApiError on failure.
export const unwrapResponse = async <T>(res: Response, fallbackMessage: string): Promise<T> =>
  (await envelope(res, fallbackMessage))?.data as T;

/**
 * Same, plus the envelope's `meta` — which `unwrapResponse` drops on the floor.
 *
 * Opt-in, and used only by the writes that report a **second** system's outcome next to a
 * successful one: the row is committed, so the failure cannot travel as a rejected promise. An
 * absent `meta` is `null`, and the shape is never trusted — the caller narrows it.
 */
export const unwrapWithMeta = async <T>(
  res: Response,
  fallbackMessage: string
): Promise<{ data: T; meta: unknown }> => {
  const body = await envelope(res, fallbackMessage);
  return { data: body?.data as T, meta: body?.meta ?? null };
};
